import { type CSSProperties, type MouseEvent, memo, useCallback, useMemo } from 'react';

import { ChevronDownIcon, ChevronRightIcon, LayoutGridIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE,
  WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE,
  workspaceAgentMentionRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionRowChrome';
import { type WorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { useWorkspaceAgentMentionRowInteraction } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionRowInteraction';

/** What each level adds to the row's left pad; a depth-0 row sits flush, as in the sidebar. */
const BOARD_TREE_INDENT_PX = 14;

/** Every library has an emoji, but a row must still read as a library if one is ever missing. */
const LIBRARY_FALLBACK_ICON = '📚';

export type WorkspaceAgentBoardTreeRowProps = {
  /** DOM id so the composer can point `aria-activedescendant` at the highlighted row. */
  id: string;
  index: number;
  /**
   * This row's own children are in flight or came back failed, so what it holds cannot be counted
   * yet. The list as a whole is not busy for it — every other row is still on offer.
   */
  isPendingChildCount: boolean;
  isDisabled?: boolean;
  /** This row holds the highlight *and* the keyboard is what last moved it, so it takes the focus ring. */
  isKeyboardHighlighted: boolean;
  isSelected: boolean;
  item: WorkspaceAgentMentionTreeItem;
  onHighlight: (index: number) => void;
  /** By id, not index: a same-query refetch between paint and click can move this item. */
  onSelect: (itemId: string) => void;
  /** Absent when the active source is not the boards tree — no other row kind opens. */
  onToggleExpanded: ((nodeId: string, isExpanded: boolean) => void) | undefined;
};

/**
 * One row of the picker's board tree, board or library. A button, like the flat mention rows, with
 * `role="treeitem"` because only a tree item can carry the level and the open state a nested list
 * has to announce. The chevron stays a span: a button cannot contain a button, and the row already
 * carries `aria-expanded` while Left/Right toggle from the composer.
 *
 * A library is a heading, not a reference — it cannot be mentioned, so it has no selected state
 * and its whole row opens and closes instead.
 *
 * Every row names itself off its title span with `aria-labelledby`, not a computed name from the
 * whole row (icons and the chevron slot would join it).
 */
export const WorkspaceAgentBoardTreeRow = memo(
  ({
    id,
    index,
    isDisabled = false,
    isKeyboardHighlighted,
    isPendingChildCount,
    isSelected,
    item,
    onHighlight,
    onSelect,
    onToggleExpanded,
  }: WorkspaceAgentBoardTreeRowProps) => {
    const isLibrary = item.kind === 'library';
    const canExpand = item.hasChildren && !!onToggleExpanded;
    const isBoardDisabled = !isLibrary && isDisabled;
    const { handleClick, handleMouseDown, handleMouseEnter, rowRef } =
      useWorkspaceAgentMentionRowInteraction<HTMLButtonElement>({
        index,
        isDisabled: isBoardDisabled,
        isSelected,
        itemId: item.id,
        onHighlight,
        onSelect,
      });

    /** Stable per row: `id` is the same DOM id the composer's `aria-activedescendant` points at. */
    const labelId = `${id}-label`;

    /** Indent and ring in one object: the row has a single `style`. Specs measure indent in geometry
     * and keyboard vs pointer highlight via `data-mention-keyboard-highlight`. */
    const rowStyle = useMemo<CSSProperties>(
      () => ({
        // A button ignores the `flex` utility's display, so two rows sit on one line.
        display: 'flex',
        paddingLeft: item.depth * BOARD_TREE_INDENT_PX,
        ...(isKeyboardHighlighted
          ? WORKSPACE_AGENT_MENTION_ROW_KEYBOARD_RING_STYLE
          : WORKSPACE_AGENT_MENTION_ROW_UNRINGED_STYLE),
      }),
      [isKeyboardHighlighted, item.depth],
    );

    const libraryBackgroundHex = item.kind === 'library' ? item.backgroundHex : undefined;
    const libraryIconStyle = useMemo<CSSProperties>(
      () => ({ backgroundColor: libraryBackgroundHex }),
      [libraryBackgroundHex],
    );

    /** Opening a board is not mentioning it, so the chevron must not reach the row's own click. */
    const handleToggleClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onToggleExpanded?.(item.id, item.isExpanded);
      },
      [item.id, item.isExpanded, onToggleExpanded],
    );

    return (
      <button
        // On the row, not the tree: a busy container defers the active-descendant announcements the
        // user is navigating the rest of the list by.
        aria-busy={isPendingChildCount}
        aria-disabled={isBoardDisabled && !canExpand ? true : undefined}
        aria-expanded={canExpand ? item.isExpanded : undefined}
        // `aria-level` is 1-based; `depth` is 0-based, so a root row is level 1.
        aria-level={item.depth + 1}
        // The title alone: icons and the chevron slot sit in the row but must not join its name.
        aria-labelledby={labelId}
        aria-posinset={item.positionInLevel}
        aria-setsize={item.siblingCount}
        aria-selected={isLibrary ? undefined : isSelected}
        className={workspaceAgentMentionRow({
          className: 'gap-1',
          isDisabled: isBoardDisabled && !canExpand,
          isSelected: isBoardDisabled ? false : isSelected,
        })}
        data-mention-depth={item.depth}
        data-mention-item-disabled={isBoardDisabled ? 'true' : undefined}
        data-mention-item-id={item.id}
        data-mention-item-kind={item.kind}
        data-mention-keyboard-highlight={String(isKeyboardHighlighted)}
        data-testid="WORKSPACE_AGENT_BOARD_MENTION_ROW"
        id={id}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        ref={rowRef}
        role="treeitem"
        style={rowStyle}
        tabIndex={-1}
        type="button"
      >
        {/* The slot is held even for a leaf, so names line up down a level rather than sawtooth. */}
        {canExpand ? (
          <span
            aria-hidden
            // `text-gray-9`, not the prototype's `gray-8`: a glyph is non-text and owes 3:1.
            className="flex size-6 shrink-0 items-center justify-center rounded-8 ads:text-icon-subtle text-gray-9 ads:hover:bg-hover-subtle hover:bg-gray-3 ads:hover:text-icon-emphasis hover:text-gray-12"
            data-testid="WORKSPACE_AGENT_BOARD_MENTION_ROW_CHEVRON"
            onClick={handleToggleClick}
            onMouseDown={handleMouseDown}
          >
            {item.isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          </span>
        ) : (
          <span aria-hidden className="size-6 shrink-0" />
        )}
        {item.kind === 'library' ? (
          /* The sidebar's library tile at row scale: the library's own colour, with its emoji on top. */
          <span
            aria-hidden
            className="flex size-5 shrink-0 items-center justify-center rounded-4 ads:bg-ads-gray-4 bg-gray-4 text-12"
            data-testid="WORKSPACE_AGENT_BOARD_MENTION_ROW_LIBRARY_ICON"
            style={libraryIconStyle}
          >
            {item.icon ?? LIBRARY_FALLBACK_ICON}
          </span>
        ) : (
          <LayoutGridIcon aria-hidden className="shrink-0 ads:text-icon-subtle text-gray-9" size={14} />
        )}
        <span
          // Bracketed leading: the prototype's row leading, which no line-height token lands on.
          // Muted title only: a view-only parent still expands, so the chevron is not faded with it.
          className={cn(
            'min-w-0 flex-1 truncate text-14 leading-[18px]',
            isBoardDisabled && 'cursor-not-allowed ads:text-metadata text-gray-9',
          )}
          data-testid="WORKSPACE_AGENT_BOARD_MENTION_ROW_TITLE"
          id={labelId}
          // On the span the row is named off, not on the row: a `title` there is announced again.
          title={item.title}
        >
          {item.title}
        </span>
      </button>
    );
  },
);

WorkspaceAgentBoardTreeRow.displayName = 'WorkspaceAgentBoardTreeRow';
